import Lenis from "lenis";

let lenisInstance: Lenis | null = null;
let rafId: number | null = null;

export const initGuitarsPage = (): void => {
  const container = document.querySelector(".guitars-table__body") as HTMLElement | null;
  if (!container) return;

  // Cleanup existing instance if any
  destroyGuitarsPage();

  // Populate 3 sets of items for seamless infinite scroll.
  // Init can run twice on first load; the marker avoids cloning an already tripled list.
  let itemCount: number;
  if (container.dataset.loopReady === "true") {
    itemCount = container.querySelectorAll(".guitars-table__row").length / 3;
    if (!itemCount) return;
  } else {
    const rows = Array.from(container.querySelectorAll(".guitars-table__row"));
    itemCount = rows.length;
    if (itemCount === 0) return;

    const set1 = rows.map((r) => r.cloneNode(true) as HTMLElement);
    const set2 = rows.map((r) => r.cloneNode(true) as HTMLElement);
    container.innerHTML = "";
    [...rows, ...set1, ...set2].forEach((row) => container.appendChild(row));
    container.dataset.loopReady = "true";
  }

  // Initialize Lenis on the scroll container
  lenisInstance = new Lenis({
    wrapper: container,
    content: container,
    duration: 1.2,
    easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    orientation: "vertical",
    gestureOrientation: "vertical",
    smoothWheel: true,
    wheelMultiplier: 1.0,
    touchMultiplier: 1.5,
  });

  const updateLoop = (time: number) => {
    if (lenisInstance) {
      lenisInstance.raf(time);
      rafId = requestAnimationFrame(updateLoop);
    }
  };
  rafId = requestAnimationFrame(updateLoop);

  // Measure single set height after DOM layout
  const calculateHeights = () => {
    const allRows = container.querySelectorAll(".guitars-table__row");
    if (allRows.length < itemCount * 3) return { singleSetHeight: 0 };

    const firstItem = allRows[0] as HTMLElement;
    const nextSetItem = allRows[itemCount] as HTMLElement;

    if (firstItem && nextSetItem && nextSetItem.offsetTop > firstItem.offsetTop) {
      return { singleSetHeight: nextSetItem.offsetTop - firstItem.offsetTop };
    }

    let singleSetHeight = 0;
    for (let i = 0; i < itemCount; i += 3) {
      singleSetHeight += (allRows[i] as HTMLElement).offsetHeight;
    }
    return { singleSetHeight };
  };

  let { singleSetHeight } = calculateHeights();

  // Set initial scroll position to start of Set 2 (middle set)
  if (singleSetHeight > 0) {
    container.scrollTop = singleSetHeight;
    lenisInstance.scrollTo(singleSetHeight, { immediate: true });
  }

  // Infinite Scroll Wrapping logic
  lenisInstance.on("scroll", ({ scroll }: { scroll: number }) => {
    if (singleSetHeight <= 0) {
      const h = calculateHeights().singleSetHeight;
      if (h > 0) singleSetHeight = h;
      else return;
    }

    // If scrolled past Set 2 into Set 3, wrap back to Set 2
    if (scroll >= singleSetHeight * 2) {
      const newScroll = scroll - singleSetHeight;
      lenisInstance?.scrollTo(newScroll, { immediate: true });
    }
    // If scrolled above Set 2 into Set 1, wrap forward to Set 2
    else if (scroll <= 0.5 * singleSetHeight) {
      const newScroll = scroll + singleSetHeight;
      lenisInstance?.scrollTo(newScroll, { immediate: true });
    }
  });

  // Re-calculate on window resize
  window.addEventListener("resize", () => {
    singleSetHeight = calculateHeights().singleSetHeight;
  });
};

export const destroyGuitarsPage = (): void => {
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  if (lenisInstance) {
    lenisInstance.destroy();
    lenisInstance = null;
  }
};
